import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  CircularProgress,
  Box,
  DialogContentText,
  FormHelperText,
  Snackbar,
  Alert
} from "@mui/material";
import { useUpdatePaymentMethodMutation } from "../../store/userApi";

interface PaymentMethodUpdateDialogProps {
  open: boolean;
  onClose: () => void;
}

// List of countries
const countries = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' }
];

export const PaymentMethodUpdateDialog: React.FC<PaymentMethodUpdateDialogProps> = ({
  open,
  onClose
}) => {
  const [updatePaymentMethod, { isLoading }] = useUpdatePaymentMethodMutation();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Form state
  const [cardNumber, setCardNumber] = useState("");
  const [expMonth, setExpMonth] = useState("");
  const [expYear, setExpYear] = useState("");
  const [cvc, setCvc] = useState("");
  const [country, setCountry] = useState("US");
  const [zipCode, setZipCode] = useState("");
  
  // Validation state
  const [errors, setErrors] = useState({
    cardNumber: false,
    expMonth: false,
    expYear: false,
    cvc: false,
    zipCode: false
  });

  const handleSubmit = async () => {
    // Validate inputs
    const newErrors = {
      cardNumber: !cardNumber || cardNumber.replace(/\s/g, '').length !== 16,
      expMonth: !expMonth || parseInt(expMonth) < 1 || parseInt(expMonth) > 12,
      expYear: !expYear || parseInt(expYear) < new Date().getFullYear(),
      cvc: !cvc || cvc.length < 3 || cvc.length > 4,
      zipCode: !zipCode
    };
    
    setErrors(newErrors);
    
    if (Object.values(newErrors).some(error => error)) {
      return;
    }
    
    try {
      await updatePaymentMethod({
        cardNumber: cardNumber.replace(/\s/g, ''),
        expMonth: parseInt(expMonth),
        expYear: parseInt(expYear),
        cvc,
        country,
        zipCode
      }).unwrap();
      
      setShowSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to update payment method');
      setShowError(true);
    }
  };
  
  const handleClose = () => {
    onClose();
  };
  
  const handleSuccessClose = () => {
    setShowSuccess(false);
  };
  
  const handleErrorClose = () => {
    setShowError(false);
  };
  
  // Generate month options
  const monthOptions = [];
  for (let i = 1; i <= 12; i++) {
    const month = i < 10 ? `0${i}` : `${i}`;
    monthOptions.push(
      <MenuItem key={month} value={month}>{month}</MenuItem>
    );
  }
  
  // Generate year options
  const yearOptions = [];
  const currentYear = new Date().getFullYear();
  for (let i = 0; i < 20; i++) {
    const year = currentYear + i;
    yearOptions.push(
      <MenuItem key={year} value={year}>{year}</MenuItem>
    );
  }

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#1a1f2e",
            color: "#ffffff"
          }
        }}
      >
        <DialogTitle>Update Payment Method</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter your new payment information below:
          </DialogContentText>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                label="Card Number"
                fullWidth
                value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value)}
                error={errors.cardNumber}
                helperText={errors.cardNumber ? "Please enter a valid 16-digit card number" : ""}
                inputProps={{ maxLength: 19 }}
                placeholder="1234 5678 9012 3456"
              />
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth error={errors.expMonth}>
                <InputLabel>Month</InputLabel>
                <Select
                  value={expMonth}
                  onChange={(e) => setExpMonth(e.target.value)}
                  label="Month"
                >
                  {monthOptions}
                </Select>
                {errors.expMonth && <FormHelperText>Required</FormHelperText>}
              </FormControl>
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth error={errors.expYear}>
                <InputLabel>Year</InputLabel>
                <Select
                  value={expYear}
                  onChange={(e) => setExpYear(e.target.value)}
                  label="Year"
                >
                  {yearOptions}
                </Select>
                {errors.expYear && <FormHelperText>Required</FormHelperText>}
              </FormControl>
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                label="CVC"
                fullWidth
                value={cvc}
                onChange={(e) => setCvc(e.target.value)}
                error={errors.cvc}
                helperText={errors.cvc ? "Invalid CVC" : ""}
                inputProps={{ maxLength: 4 }}
              />
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  label="Country"
                >
                  {countries.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="ZIP Code"
                fullWidth
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value)}
                error={errors.zipCode}
                helperText={errors.zipCode ? "ZIP code is required" : ""}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleClose}
            sx={{ color: "rgba(255,255,255,0.7)" }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : "Update Payment Method"}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar open={showSuccess} autoHideDuration={6000} onClose={handleSuccessClose}>
        <Alert onClose={handleSuccessClose} severity="success">
          Payment method updated successfully!
        </Alert>
      </Snackbar>
      
      <Snackbar open={showError} autoHideDuration={6000} onClose={handleErrorClose}>
        <Alert onClose={handleErrorClose} severity="error">
          {errorMessage || 'Failed to update payment method'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PaymentMethodUpdateDialog; 